<template>    
  <form class="app" @submit.prevent="onLogin" novalidate autocomplete="off">
    <p v-if="selectId == '3' || businessSelectId == '3'" aria-label="* required field" class="app-required-indicator __before req-field">required field</p>
    <p v-else aria-label="All fields are required" class="req-field">All fields are required.</p>
    <div class="app-input-row">
      <!-- Business users -->
      <div class="app-input-row" v-if="showBusinessFields">
        <div class="app-input-group" :class="{ 'app-invalid-group': !isOrgNameValid }">
          <label :for="businessNameField" class="inputclass" :class="{ 'app-required-indicator __after': businessSelectId == '3' }">
            Organization name
            <span class="subText" v-if="businessSubText">
              {{ businessSubText }}
            </span>
          </label>
          <Tooltip ref="orgTooltip" theme="my-custom-tooltip" :triggers="[]" >
            <input type="text" :id="businessNameField" v-model="orgName" @input="validateOrgName"
              @mouseenter="showTooltip" @mouseleave="hideTooltip" @focusin="showTooltip"
              @focusout="hideTooltip" aria-required="true"
              autocomplete="off" class="org-input-class" :aria-describedby="`${businessNameField}Desc`"/>
            <template #popper>
              <span role="tooltip" aria-hidden="true">{{ tooltipText }}</span>
            </template>
          </Tooltip>
          <p v-if="!isOrgNameValid" class="app-error-message ">{{ orgNameError }}</p>
          <p :id="`${businessNameField}Desc`" class="sr-only">{{ getFieldDescription(`${businessNameField}`) }}</p>
          <p :id="`${businessNameField}Desc`" class="sr-only">Required. Up to 40 characters. Do not use comma, semicolon, less-than sign or pipe.</p>
        </div>
      </div>

      <!-- Individual users -->
      <!-- Last Name -->
      <div class="app-input-group" :class="{ 'app-invalid-group': !isNameValid }" v-if="showNameFields">
        <label :for="nameField" class="inputclass" :class="{'app-required-indicator __after': selectId == '3'}">Last name <span class="subText"  v-if="selectId == '1'">(as on your License / ID Card)</span></label>
        <input type="text" :id="nameField" :aria-describedby="`${nameField}Desc`"
              v-model="myName" @input="validateName" aria-required="true" autocomplete="off"/>
        <p v-if="!isNameValid" class="app-error-message">{{ nameError }}</p>
        <p :id="`${nameField}Desc`" class="sr-only">{{ getFieldDescription(`${nameField}`) }}</p>
        <p :id="`${nameField}Desc`" class="sr-only">Required. Up to 40 characters. Do not use comma, semicolon, less-than sign or pipe.</p>
      </div>

      <!-- DOB -->    
      <div class="app-input-group" :class="{ 'app-invalid-group': !isDateSupported }" v-if="showDobFields">
        <label :for="dateField" class="inputclass" :class="{'app-required-indicator __after': selectId == '3'}">Date of birth (MM/DD/YYYY)</label>
        <div class="date-input-wrapper">
          <input type="text" placeholder="MM/DD/YYYY" :id="dateField" :aria-describedby="`${dateField}Desc`"
                v-model="myDob" @input="onDateInput($event, 'myDob')" aria-required="true" autocomplete="off" />
          <button type="button" class="app-btn-image app-calendar-btn" @click="triggerDatePicker(dateField)" :aria-label="`Open calendar for ${dateField}`">
            <input type="date" :id="`${dateField}Picker`" v-model="myDobForPicker" @change="updateDobFromPicker" :max="todayDate" class="hidden-date-picker" tabindex="-1" />
            <img src="@ridmv/web-themes/images/icons/calendar-solid.svg" alt="Open calendar">
            <span class="sr-only">Open calendar</span>
          </button>
        </div>
        <p v-if="!isDateSupported" class="app-error-message">{{ dobError }}</p>
        <p :id="`${dateField}Desc`" class="sr-only">{{ getFieldDescription(`${dateField}`) }}</p>
        <p id="dobDesc" class="sr-only">Required in mm/dd/yyyy format.</p>
      </div>

      <!-- SSN -->
      <div class="app-input-group" :class="{ 'app-invalid-group': !isSsnValid }" v-if="showSsnField">
        <label :for="ssnField">Last four digits of SSN</label>
        <input class="fourdigitssn" :type="inputType" maxlength="4" @input="onlyNumbers($event, 'myLastSsn')" :id="ssnField" :aria-describedby="`${ssnField}Desc`"
              v-model="myLastSsn" aria-required="true" autocomplete="off" />
        <button type="button" class="app-btn-image" @click='toggleHidden()'>
          <img src="@ridmv/web-themes/images/icons/eye-solid.svg" alt="Show or Hide last four digits of ssn">
          <span class="sr-only">Show or hide last four of ssn</span>
        </button>
        <p v-if="!isSsnValid" class="app-error-message">{{ ssnError }}</p>
        <p :id="`${ssnField}Desc`" class="sr-only">{{ getFieldDescription(`${ssnField}`) }}</p>
      </div>
      
      <!-- Case/Ticket  Number-->
      <div class="app-input-group" :class="{ 'app-invalid-group': !isCaseNumberValid }" v-if="showCaseNumberField" >
        <div class="d-flex">
          <label :for="caseNumberField" class="inputclass app-required-indicator __after"> 
            Case / Summons / Ticket number
          </label>
        </div>
        <input type="text" :id="caseNumberField" :aria-describedby="`${caseNumberField}Desc`"
              v-model="myCaseNumber" @input="validateCaseNumber" aria-required="true" autocomplete="off"/>
        <p v-if="!isCaseNumberValid" class="app-error-message">{{ caseNumberError }}</p>
        <p :id="`${caseNumberField}Desc`" class="sr-only">{{ getFieldDescription(`${caseNumberField}`) }}</p>
        <p :id="`${caseNumberField}Desc`" class="sr-only">Required. Up to 18 characters.</p>
      </div>
    </div>

    <div class="app-input-row"  v-if="showLicenseField">
      <!-- License -->
      <div class="app-input-group" :class="{ 'app-invalid-group': !isLicenseValid }">
      <label :for="licenseField" class="inputclass">RI License / ID #</label>
      <input type="text" maxlength="8" :id="licenseField" :aria-describedby="`${licenseField}Desc`"
            v-model="myLicense" @input="validateLicense" aria-required="true" autocomplete="off" />
      <p v-if="!isLicenseValid" class="app-error-message">{{ licenseError }}</p>
      <p :id="`${licenseField}Desc`" class="sr-only">{{ getFieldDescription(`${licenseField}`) }}</p>
      <p :id="`${licenseField}Desc`" class="sr-only">Required. Up to 8 characters. Do not use comma, semicolon, less-than sign or pipe.</p>
      </div>
    </div>

    <!-- /* This div will open when user selects vehicle type*/ -->
    <div class="app-input-row" v-if="showVehicleField">
      <div class="app-input-group " v-show="businessSelectId === '2'">
        <label :for="idField">ID type</label>
        <select class="app-custom-select" v-model="myPlateIdType" aria-required="true"
          autocomplete="off" :id="idField" :aria-describedby="`${idField}Desc`">
          <option value="plate"> Plate # </option>
          <option value="title"> Title # </option>
        </select>
      </div>

      <!-- Plate Number -->
      <div class="app-input-group " :class="{ 'app-invalid-group': !isPlateValid }">
        <label :for="plateField">{{ labelText }}</label>
        <input class="plate" type="text" :maxlength="plateMaxLength" :id="plateField" :aria-describedby="`${plateField}Desc`"
              v-model.trim="myPlateNumber" @input="validatePlate" aria-required="true" autocomplete="off"/>
        <p v-if="!isPlateValid" class="app-error-message">{{ plateError }}</p>
        <p :id="`${plateField}Desc`" class="sr-only">{{ getFieldDescription(`${plateField}`) }}</p>
      </div>

      <!-- Registration Type -->
      <div class="app-input-group " :class="{ 'app-invalid-group': !isTypeValid }" v-if="(myPlateIdType === 'plate' || businessSelectId !== '2')" >
        <label :for="typeField">Plate type</label>
        <select class="app-custom-select" :id="typeField" :aria-describedby="`${typeField}Desc`"
                v-model.lazy="myPlateType" @change="validateType" aria-required="true" autocomplete="off">
          <option :value="{ code: 'none' }">Select a type</option>
          <option v-for="type in registrationTypes" :value="{ code: type.vehiclePlateTypeId, description: type.description }" :key="type.vehiclePlateTypeId">
            {{ type.description }}
          </option>
        </select>
        <p v-if="!isTypeValid" class="app-error-message">{{ typeError }}</p>
        <p :id="`${typeField}Desc`" class="sr-only">{{ getFieldDescription(`${typeField}`) }}</p>
      </div>

      <!-- Last 6 VIN Digits -->
      <div class="app-input-group" :class="{ 'app-invalid-group': !isVinValid }">
        <label :for="vinField">Last six digits of VIN</label>
        <input class="vin" type="text" maxlength="6" :id="vinField" :aria-describedby="`${vinField}Desc`"
              v-model.trim="myLastVin" @input="validateVin" aria-required="true" autocomplete="off"/>
        <p v-if="!isVinValid" class="app-error-message">{{ vinError }}</p>
        <p :id="`${vinField}Desc`" class="sr-only">{{ getFieldDescription(`${vinField}`) }}</p>
      </div>
    </div>

    <!-- /* This div will open when user selects violation/suspension*/ -->
    <p v-if="showViolationSuspensionField"> Please enter one of the following</p>
    <div class="app-input-row" v-if="showViolationSuspensionField">
      <div class="app-input-group app-input-model" :class="{ 'app-invalid-group': !isViolationDateSupported }">
        <label :for="violationDateField" class="inputclass">Violation date / Offense date (MM/DD/YYYY)</label>
        <div class="date-input-wrapper">
          <input type="text" placeholder="MM/DD/YYYY" :id="violationDateField" :aria-describedby="`${violationDateField}Desc`"
                v-model="myViolationDate" @input="onDateInput($event, 'myViolationDate')" aria-required="true" autocomplete="off" />
          <button type="button" class="app-btn-image app-calendar-btn" @click="triggerDatePicker(violationDateField)" :aria-label="`Open calendar for ${violationDateField}`">
            <input type="date" :id="`${violationDateField}Picker`" v-model="myViolationDateForPicker" @change="updateViolationDateFromPicker" :max="todayDate" class="hidden-date-picker" tabindex="-1" />
            <img src="@ridmv/web-themes/images/icons/calendar-solid.svg" alt="Open calendar">
            <span class="sr-only">Open calendar</span>
          </button>
        </div>
      </div>
      <div class="conjunction">OR</div>
      <div class="app-input-group app-input-model" :class="{ 'app-invalid-group': !isViolationDateSupported }">
        <label :for="suspensionDateField" class="inputclass">Suspension effective date (MM/DD/YYYY)</label>
        <div class="date-input-wrapper">
          <input type="text" placeholder="MM/DD/YYYY" :id="suspensionDateField" :aria-describedby="`${suspensionDateField}Desc`"
                v-model="mySuspensionDate" @input="onDateInput($event, 'mySuspensionDate')" aria-required="true" autocomplete="off" />
          <button type="button" class="app-btn-image app-calendar-btn" @click="triggerDatePicker(suspensionDateField)" :aria-label="`Open calendar for ${suspensionDateField}`">
            <input type="date" :id="`${suspensionDateField}Picker`" v-model="mySuspensionDateForPicker" @change="updateSuspensionDateFromPicker" class="hidden-date-picker" tabindex="-1" />
            <img src="@ridmv/web-themes/images/icons/calendar-solid.svg" alt="Open calendar">
            <span class="sr-only">Open calendar</span>
          </button>
        </div>
      </div>
      <p v-if="!isViolationDateSupported" class="app-error-message-violation-suspension app-error-group">{{ violationError }}</p>
      <p :id="`${violationDateField}Desc`" class="sr-only">{{ getFieldDescription(`${violationDateField}`) }}</p>
      <p id="violationDesc" class="sr-only">Required in mm/dd/yyyy format.</p>
    </div>
    <div class="app-input-empty-row" v-if="selectId == '3' || businessSelectId == '3'"></div>

    <!-- /* This div will open when user selects crash record*/ -->
    <div class="app-input-row"  v-if="showCrashField">
      <div class="app-input-group" :class="{ 'app-invalid-group': !isCrashDateSupported }">
        <label :for="crashDateField" class="inputclass">Date of crash (MM/DD/YYYY)</label>
        <div class="date-input-wrapper">
          <input type="text" placeholder="MM/DD/YYYY" :id="crashDateField" :aria-describedby="`${crashDateField}Desc`"
                v-model="myCrashDate" @input="onDateInput($event, 'myCrashDate')" aria-required="true" autocomplete="off" />
          <button type="button" class="app-btn-image app-calendar-btn" @click="triggerDatePicker(crashDateField)" :aria-label="`Open calendar for ${crashDateField}`">
            <input type="date" :id="`${crashDateField}Picker`" v-model="myCrashDateForPicker" @change="updateCrashDateFromPicker" :max="todayDate" class="hidden-date-picker" tabindex="-1" />
            <img src="@ridmv/web-themes/images/icons/calendar-solid.svg" alt="Open calendar">
            <span class="sr-only">Open calendar</span>
          </button>
        </div>
        <p v-if="!isCrashDateSupported" class="app-error-message">{{ crashDateError }}</p>
        <p :id="`${crashDateField}Desc`" class="sr-only">{{ getFieldDescription(`${crashDateField}`) }}</p>
        <p id="crashDateDesc" class="sr-only">Required in mm/dd/yyyy format.</p>
      </div>

      <div class="app-input-group" :class="{ 'app-invalid-group': !isCrashLocationValid }">
        <label :for="crashLocationField" class="inputclass">Crash location</label>
        <select class="app-custom-select" :id="crashLocationField" :aria-describedby="`${crashLocationField}Desc`"
                v-model="myCrashLocation" @change="validateCrashLocation" aria-required="true" autocomplete="off">
          <option :value="{ code: 'none' }">Select</option>
          <option v-for="type in crashLocations" :value="{ code: type.cityId, description: type.description }" :key="type.cityId">
            {{ type.description }}
          </option>
        </select>
        <p v-if="!isCrashLocationValid" class="app-error-message">{{ crashLocationError }}</p>
        <p :id="`${crashLocationField}Desc`" class="sr-only">{{ getFieldDescription(`${crashLocationField}`) }}</p>
      </div>
    </div>

    <BaseButtonGroup>
      <CustomButton @click="onLogin">
        Sign in
      </CustomButton>        
    </BaseButtonGroup>
  </form>
</template>


<script setup>
import * as COMMONS from '../../../store/_commons'
import vu from '@/utils/validationUtils'
import BaseButtonGroup from '@/components/ui/BaseButtonGroup'
import { isAfter, parse, toDate } from "date-fns";
import CustomButton from '../../CustomButton.vue';
import { computed,ref, reactive, onMounted, onUnmounted, watch } from 'vue';
import { useStore } from 'vuex';
import { onBeforeRouteUpdate } from 'vue-router';
import { Tooltip } from 'floating-vue';
import '@/assets/tooltip.css';

const props = defineProps({
  selectId: String,
  businessSelectId: String 
});

const sourceReferrer = ref( '');

// Log referrer only if it matches the reservation origin
const getReservationReferrerOnly = () => {
  if(!document.referrer)
  return '';
  try {
    const referrerOrigin = new URL(document.referrer).origin;
    const reservationOrigin = new URL(window?.config?.WEB_CLIENT_URL || '') .origin;
    return referrerOrigin === reservationOrigin ? document.referrer : '';
  } catch {
    return '';
  }
}
// 1. Field Names
const businessNameField = ref('orgName');
const nameField = ref('lastName');
const ssnField = ref('lastFourDigitsOfSsn');
const dateField = ref('dateOfBirth');
const inputType = ref('password');
const licenseField = ref('licenseNumber');
const ddNumberField = ref('ddNumber');

const plateField = ref('plateNumber');
const typeField = ref('plateType');
const vinField = ref('vinNumberLastSix');

const idField = ref('idType');

const caseNumberField = ref('caseNumber');
const suspensionDateField = ref('suspensionDate');
const violationDateField = ref('violationDate');
const crashDateField = ref('crashDate');
const crashLocationField = ref('crashLocation');

const patterns = reactive({
  namePattern: /^[a-zA-Z\d!@#$%^&*()_+=[\]{}\u2018\u2019':"\\.>/?`~ ]*$/,
  caseNumPattern: /^[a-zA-Z\d!@#$%^&*()_+-=[\]{}\u2018\u2019':"\\.>/?|`~ ]*$/,
  numericPattern: /^\d+$/,
  inputPattern: /^[a-zA-Z\d]*$/,
});

// 2. User Input Fields
const orgName = ref('');
const myName = ref('');
const myLastSsn = ref('');
const myDob = ref(undefined);
const myDobForPicker = ref('');
const myLicense = ref('');
const myLastVin = ref('');

const myPlateIdType = ref('plate');   // default for business users
const myPlateType = ref({ code: 'none' }); // default for plate type dropdown

const plateNumber = ref('');
const titleNumber = ref('');

const myCaseNumber = ref('');
const myViolationDate = ref(undefined);
const myViolationDateForPicker = ref('');
const mySuspensionDate = ref(undefined);
const mySuspensionDateForPicker = ref('');
const myCrashDate = ref(undefined);
const myCrashDateForPicker = ref('');
const myCrashLocation = ref({ code: 'none' }); 

const isOrgNameValid = ref(true);
const isNameValid = ref(true);
const isSsnValid = ref(true);
const isDateSupported = ref(true);
const isModify = ref(false);
const errorCode = ref(undefined);
const isLicenseValid = ref(true);
const isddNumberValid = ref(true);

const isPlateValid = ref(true);
const isTypeValid = ref(true);
const isVinValid = ref(true);
const isCaseNumberValid = ref(true);
const isViolationDateSupported = ref(true);
const isSuspensionDateSupported = ref(true);
const isCrashDateSupported = ref(true);
const isCrashLocationValid = ref(true);

const violationErrorCode = ref(undefined);
const crashDateErrorCode = ref(undefined);

const orgTooltip = ref(null);
const tooltipText = ref('');

// 1. Vuex Getters
const lookupInfo = computed(() => store.getters[COMMONS.VEHICLE_LOOKUP_INFO]);
const crashLookupInfo = computed(() => store.getters[COMMONS.GUEST_LOGIN_LOOKUP_INFO]);

const labelText = computed(() => {
  // Individual users 
  if (props.businessSelectId !== '2') {
    return 'Plate number';
  }

  // Business users
  if (myPlateIdType.value === 'title') return 'Title number';
  return 'Plate number'; // default for business users
});

const plateMaxLength = computed(() => {
  return myPlateIdType.value === 'title' ? 8 : 7;
});

watch(myPlateIdType, () => {
  // Clear plate number/title number and VIN when switching between Title # and Plate #
  plateNumber.value = '';
  titleNumber.value = '';
  myLastVin.value = '';

  // Reset validation flags to hide error messages
  isOrgNameValid.value = true;
  isPlateValid.value = true;
  isVinValid.value = true;
  isTypeValid.value = true;

  // reset plate type when switching to Title #
  if (myPlateIdType.value === 'title') {
    myPlateType.value = { code: 'none' };
  }
});

const myPlateNumber = computed({
  get() {
    return myPlateIdType.value === 'plate' ? plateNumber.value : titleNumber.value;
  },
  set(val) {
    if (myPlateIdType.value === 'plate') {
      plateNumber.value = val;
    } else {
      titleNumber.value = val;
    }
  }
});


// Combined logic for individual and business users
const showBusinessFields = computed(() =>
  ["2", "3", "4"].includes(props.businessSelectId)
);

const showNameFields = computed(() =>
  ["1", "2", "3", "4"].includes(props.selectId)
);

const showDobFields = computed(() =>
  ["1", "2", "3", "4"].includes(props.selectId)
);

const showSsnField = computed(() => props.selectId === "1");

const showCaseNumberField = computed(() =>
  props.selectId === "3" || props.businessSelectId === "3"
);

const showLicenseField = computed(() => props.selectId === "1");

const showVehicleField = computed(() =>
  props.selectId === "2" || props.businessSelectId === "2"
);

const showViolationSuspensionField = computed(() =>
  props.selectId === "3" || props.businessSelectId === "3"
);

const showCrashField = computed(() =>
  props.selectId === "4" || props.businessSelectId === "4"
);

const businessSubText = computed(() => {
  const showText = {
    2: "(As printed on your Vehicle Registration / Title)",
    3: "(As printed on the RI DMV's Notice of Action)",
    4: "(As printed on the RI Police crash report)"
  };
  return showText[props.businessSelectId] || "";
});

// Computed Properties for Dropdown Options and Validation Messages
const registrationTypes = computed(() => {
  return lookupInfo.value.allRegistrationTypes;
});

const crashLocations = computed(() => {
  let sortCityName = [];
  if (crashLookupInfo.value.allCrashLocation) {
    sortCityName = crashLookupInfo.value.allCrashLocation.slice().sort((a, b) => {
      return a.description.localeCompare(b.description);
    });
  }
  return sortCityName;
});

const orgNameError = computed(() => {
  const isOrgNameValid = (orgName.value.length > 0) && patterns.namePattern.test(orgName.value) && orgName.value.length < 80;
  if (!isOrgNameValid) {
    const val = orgName.value;

    let errorCode;
    if (val.length <= 0) {
      errorCode = '17';       
    }

    if (!(patterns.namePattern.test(val))) {
      errorCode = '18';       
    }
    if (orgName.value.length > 80) {
      errorCode = '19';
    }
    return getFieldErrorMessage(businessNameField.value, errorCode);
  }

  return '';
});

const todayDate = computed(() => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
});

const nameError = computed(() => {
  const isNameValid = (myName.value.length > 0) && patterns.namePattern.test(myName.value) && myName.value.length < 40;
  if (!isNameValid) {
    const val = myName.value;

    let errorCode;
    if (val.length <= 0) {
      errorCode = '01';       
    }

    if (!(patterns.namePattern.test(val))) {
      errorCode = '02';       
    }
    if (myName.value.length > 40) {
      errorCode = '03';
    }
    return getFieldErrorMessage(nameField.value, errorCode);
  }

  return '';
});

const ssnError = computed(() => {
  if (!isSsnValid.value) { 
    return getFieldErrorMessage(ssnField.value); 
  }
  return '';
});

const dobError = computed(() => {
  if (!isDateSupported.value) { 
    return getFieldErrorMessage(dateField.value, errorCode.value); 
  }
  return '';
});

const licenseError = computed(() => {
  const isLicenseValid = (myLicense.value.length > 6) && patterns.namePattern.test(myLicense.value) && myLicense.value.length <= 8;
  if (!isLicenseValid) {
    const val = myLicense.value; 

    let errorCode;
    if (val.length < 7) {
      errorCode = '07';       
    }

    if (!(patterns.namePattern.test(val))) { 
      errorCode = '07';       
    }
    return getFieldErrorMessage(licenseField.value, errorCode); 
  }

  return '';
});

const plateErrorType = computed(() => {
  return myPlateIdType.value === 'title' ? 'titleNumber' : 'plateNumber';
});

const plateError = computed(() => {
  if (!isPlateValid.value) { 
    return getFieldErrorMessage(plateErrorType.value); 
  }
  return '';
});

const typeError = computed(() => {
  if (!isTypeValid.value) { 
    return getFieldErrorMessage(typeField.value); 
  }
  return '';
});

const vinError = computed(() => {
  if (!isVinValid.value) { 
    return getFieldErrorMessage(vinField.value); 
  }
  return '';
});

const caseNumberError = computed(() => {
  const isCaseNumberValid = (myCaseNumber.value.length > 0) && patterns.caseNumPattern.test(myCaseNumber.value) && myCaseNumber.value.length <= 18 ;

  if (!isCaseNumberValid) {
    const val = myCaseNumber.value; 

    let errorCode;
    if (val.length <= 0) {
      errorCode = '10';       
    }

    if (myCaseNumber.value.length > 18) {
      errorCode = '11';
    }
    return getFieldErrorMessage(caseNumberField.value, errorCode); 
  }

  return '';
});

const violationError = computed(() => {
  if (!isViolationDateSupported.value) { 
    return getFieldErrorMessage(violationDateField.value, violationErrorCode.value); 
  }
  return '';
});

const crashDateError = computed(() => {
  if (!isCrashDateSupported.value) { 
    return getFieldErrorMessage(crashDateField.value, crashDateErrorCode.value); 
  }
  return '';
});

const crashLocationError = computed(() => {
  if (!isCrashLocationValid.value) { 
    return getFieldErrorMessage(crashLocationField.value);
  }
  return '';
});

const validationMessages = [
  { ref: nameField.value, code: '01', message: 'Last name is required' },
  { ref: nameField.value, code: '02',  message: 'Special characters ";", "<", "|", "," and "-" are not allowed.' + 'For hyphenated names please use a space in place of hyphen / dash.'},
  { ref: nameField.value, code: '03', message: 'Last name is limited to 40 characters' },
  { ref: ssnField.value,  code: '04', message: 'Last four digits of SSN is required'},  
  { ref: dateField.value, code: '05', message: 'A valid Date of birth is required' },
  { ref: dateField.value, code: '06', message: 'Date must be within the acceptable range.' },
  { ref: licenseField.value, code: '07', message: 'A valid RI License # or State ID # is required' },
  { ref: ddNumberField.value, code: '08', message: 'A valid DD # is required. Please refer to the image for help on where to locate the DD # on your License / ID card.' },
  { ref: ddNumberField.value, code: '09', message: 'DD # is limited to 10 characters. Please refer to the image for help on where to locate the DD # on your License / ID card.' },  
  { ref: 'plateNumber', message: 'Plate number is required. ' +
          'Acceptable characters are letters or numbers. Please do not include dashes or spaces.' },
  { ref: 'titleNumber', message: 'Title number is required. ' +
          'Acceptable characters are letters or numbers. Please do not include dashes or spaces.' },
  { ref: typeField.value, message: 'Plate type is required.' },
  { ref: vinField.value, message: 'Last six digits of VIN are required, you must enter all six digits. ' +
          'Acceptable characters are letters or numbers.' },   
  { ref: caseNumberField.value, code: '10', message: 'Case / Summons / Ticket number is required' }, 
  { ref: caseNumberField.value, code: '11', message: 'Case / Summons / Ticket number is limited to 18 characters' },  
  { ref: violationDateField.value, code: '12', message: 'A valid Violation / Offense date ' + 'OR' + ' Suspension effective date is required' },  
  { ref: crashDateField.value, code: '14', message: 'A valid Date of crash is required' },  
  { ref: crashDateField.value, code: '15', message: 'Date of crash should not be a future date' }, 
  { ref: crashLocationField.value, code: '16', message: 'Crash location is required' },
  { ref: businessNameField.value, code: '17', message: 'Organization name is required' },
  { ref: businessNameField.value, code: '18',  message: 'Special characters ";", "<", "|", "," and "-" are not allowed.' + 'For hyphenated names please use a space in place of hyphen / dash.'},
  { ref: businessNameField.value, code: '19', message: 'Organization name is limited to 80 characters' },
];

const store = useStore(); 

// 1. Vuex Actions 
const setGuestLoginData = (payload) => store.dispatch(COMMONS.GUEST_LOGIN_SET_DATA, payload);
const getGuestLogout = (payload) => store.dispatch(COMMONS.GUEST_LOGIN_GET_LOGOUT, payload);
const getTypeList = (payload) => store.dispatch(COMMONS.VEHICLE_GET_TYPE_LIST, payload);
const getCrashLocation = (payload) => store.dispatch(COMMONS.GUEST_CRASH_LOCATION, payload);
const getGuestLoginRequest = (payload) => store.dispatch(COMMONS.GUEST_LOGIN_GET_LOGIN_REQUEST, payload);

const getLogout = () => {
  getGuestLogout();
};

// Code that would typically be in 'created()' goes here, at the top level of <script setup>
getTypeList(); 
getCrashLocation(); 
if (store.state.guestLogin.accessToken) { 
  getLogout(); 
}

// Custom Methods
const getFieldErrorMessage = (ref, code = undefined) => {
  return vu.getFieldValidationMessage(validationMessages.value || validationMessages, ref, code); //
};

const getFieldDescription = (ref) => {
  return vu.getFieldAccessibleDescription(validationMessages.value || validationMessages, ref);
};

const onlyNumbers = (event, vModel) => {
  if (vModel === 'myLastSsn') { 
    myLastSsn.value = event.target.value.replaceAll(/\D/g, '');
    validateSsn(); 
  }
};

const toggleHidden = () => {
  inputType.value = inputType.value === "password" ? "text" : "password";       
};

const showTooltip = () => {
  if (orgName.value) {
    tooltipText.value = orgName.value;
    orgTooltip.value?.show();
  }
};

const hideTooltip = () => {
  orgTooltip.value?.hide();
};

const triggerDatePicker = (fieldId) => {
  const pickerId = `${fieldId}Picker`;
  const pickerElement = document.getElementById(pickerId);
  if (pickerElement) {
    // Trigger click on the date input to open the picker
    pickerElement.click();
    // Try showPicker if available (modern browsers)
    if (pickerElement.showPicker) {
      try {
        pickerElement.showPicker();
      } catch (e) {
        // Fallback already handled by click
        console.log('showPicker not supported, using click fallback', e);
      }
    }
  }
};

const formatDateInput = (value) => {
  if (!value) return '';
  // Remove all non-numeric characters
  const numbersOnly = value.replaceAll(/\D/g, '');

  // Limit to 8 digits (MMDDYYYY)
  const limited = numbersOnly.slice(0, 8);

  // Format as MM/DD/YYYY
  if (limited.length <= 2) {
    return limited;
  } else if (limited.length <= 4) {
    return `${limited.slice(0, 2)}/${limited.slice(2)}`;
  } else {
    return `${limited.slice(0, 2)}/${limited.slice(2, 4)}/${limited.slice(4, 8)}`;
  }
};

const onDateInput = (event, refName) => {
  const formatted = formatDateInput(event.target.value);

  if (refName === 'myDob') {
    myDob.value = formatted;
    validateDob();
  } else if (refName === 'myViolationDate') {
    myViolationDate.value = formatted;
    validateViolationSuspensionDate();
  } else if (refName === 'mySuspensionDate') {
    mySuspensionDate.value = formatted;
    validateViolationSuspensionDate();
  } else if (refName === 'myCrashDate') {
    myCrashDate.value = formatted;
    validateCrashDate();
  }
};

const convertToTextFormat = (dateString) => {
  if (!dateString) return '';
  // Convert YYYY-MM-DD to MM/DD/YYYY
  const [year, month, day] = dateString.split('-');
  return `${month}/${day}/${year}`;
};

const updateDobFromPicker = () => {
  if (myDobForPicker.value) {
    // Check if selected date is in the future
    const selectedDate = new Date(myDobForPicker.value);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    selectedDate.setHours(0, 0, 0, 0);
    
    if (selectedDate > today) {
      // Future date not allowed, clear the picker and text field
      myDobForPicker.value = '';
      myDob.value = '';
      validateDob();
      return;
    }
    
    myDob.value = convertToTextFormat(myDobForPicker.value);
    validateDob();
  } else {
    myDob.value = '';
    validateDob();
  }
};

const updateViolationDateFromPicker = () => {
  if (myViolationDateForPicker.value) {
    // Check if selected date is in the future
    const selectedDate = new Date(myViolationDateForPicker.value);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    selectedDate.setHours(0, 0, 0, 0);
    
    if (selectedDate > today) {
      // Future date not allowed, clear the picker and text field
      myViolationDateForPicker.value = '';
      myViolationDate.value = '';
      validateViolationSuspensionDate();
      return;
    }
    
    myViolationDate.value = convertToTextFormat(myViolationDateForPicker.value);
    validateViolationSuspensionDate();
  } else {
    myViolationDate.value = '';
    validateViolationSuspensionDate();
  }
};

const updateSuspensionDateFromPicker = () => {
  if (mySuspensionDateForPicker.value) {
    // Check if selected date is in the future
    const selectedDate = new Date(mySuspensionDateForPicker.value);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    selectedDate.setHours(0, 0, 0, 0);
      
    mySuspensionDate.value = convertToTextFormat(mySuspensionDateForPicker.value);
    validateViolationSuspensionDate();
  } else {
    mySuspensionDate.value = '';
    validateViolationSuspensionDate();
  }
};

const updateCrashDateFromPicker = () => {
  if (myCrashDateForPicker.value) {
    // Check if selected date is in the future
    const selectedDate = new Date(myCrashDateForPicker.value);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    selectedDate.setHours(0, 0, 0, 0);
    
    if (selectedDate > today) {
      // Future date not allowed, clear the picker and text field
      myCrashDateForPicker.value = '';
      myCrashDate.value = '';
      validateCrashDate();
      return;
    }
    
    myCrashDate.value = convertToTextFormat(myCrashDateForPicker.value);
    validateCrashDate();
  } else {
    myCrashDate.value = '';
    validateCrashDate();
  }
};

const convertTextToDateFormat = (dateString) => {
  if (!dateString) return '';
  // Convert MM/DD/YYYY to YYYY-MM-DD for backend submission
  if ((dateString?.match(/\d{2}\/\d{2}\/\d{4}$/g) || []).length === 1) {
    const [month, day, year] = dateString.split('/');
    return `${year}-${month}-${day}`;
  }
  return dateString;
};

const onLogin = () => { 
  const individualType = Number(props.selectId);
  const businessType = Number(props.businessSelectId);

  if (individualType > 0) {
    onLoginIndividual(individualType);
  } else if (businessType > 0) {
    onLoginBusiness(businessType);
  }
};

// INDIVIDUAL LOGIN
const onLoginIndividual = (individualType) => {
  if (![1, 2, 3, 4].includes(individualType)) {
    return;
  }

  if (!validate()) return;

  setGuestLoginData({
    loginMode: "individual",
    loginTypeId: individualType,
    customerName: myName.value,
    dateOfBirth: convertTextToDateFormat(myDob.value),
    lastFourDigitsOfSsn: myLastSsn.value,
    licenseNumber: myLicense.value,
    plateIdType: 'plate',    // always 'plate' for individual users
    plateNumber: myPlateNumber.value,
    plateType: myPlateType.value,
    vinLastSix: myLastVin.value,
    caseSummonTicketNumber: myCaseNumber.value,
    suspensionEffectiveDate: convertTextToDateFormat(mySuspensionDate.value),
    violationDate: convertTextToDateFormat(myViolationDate.value),
    crashDate: convertTextToDateFormat(myCrashDate.value),
    crashLocation: myCrashLocation.value,
    sourceReferrer: sourceReferrer.value,  // for tracking where the user came from
  });

  return getGuestLoginRequest();
};

// BUSINESS LOGIN
const onLoginBusiness = (businessType) => {
  if (![2, 3, 4].includes(businessType)) {
    return;
  }

  if (!validateBusiness()) return;

  setGuestLoginData({
    loginMode: "business",
    loginTypeId: businessType,
    customerName: orgName.value,
    plateIdType: myPlateIdType.value,
    plateNumber: myPlateIdType.value === 'plate' ? myPlateNumber.value : null,
    titleNumber: myPlateIdType.value === 'title' ? myPlateNumber.value : null,
    plateType: myPlateIdType.value === 'plate' ? myPlateType.value : null,
    vinLastSix: myLastVin.value,
    caseSummonTicketNumber: myCaseNumber.value,
    suspensionEffectiveDate: convertTextToDateFormat(mySuspensionDate.value),
    violationDate: convertTextToDateFormat(myViolationDate.value),
    crashDate: convertTextToDateFormat(myCrashDate.value),
    crashLocation: myCrashLocation.value,
    sourceReferrer: sourceReferrer.value,  // for tracking where the user came from
  });

  return getGuestLoginRequest();
};

const validateOrgName = () => {
  const val = orgName.value; 
  isOrgNameValid.value = (val.length > 0) && patterns.namePattern.test(val) && val.length <= 80;
};

const validateName = () => {
  const val = myName.value; 
  isNameValid.value = (val.length > 0) && patterns.namePattern.test(val) && val.length <= 40;
};

const validateSsn = () => {
  const val = myLastSsn.value; 
  isSsnValid.value = (val.length === 4);
};

const validateDob = () => {
  errorCode.value = undefined; 
  let value = myDob.value;    

  if (value == null || value == '') {
    errorCode.value = '05';
  } else {
    // Check for "MM/DD/YYYY" format and convert to "YYYY-MM-DD" if found
    if ((value?.match(/\d{2}\/\d{2}\/\d{4}$/g) || []).length === 1) {
      const tmp = value;
      value = `${tmp.substr(6, 4)}-${tmp.substr(0, 2)}-${tmp.substr(3, 2)}`;
    }

    const minDate = toDate(new Date(1751, 11, 31, 23, 59, 59));
    const curDate = parse(value, 'yyyy-MM-dd', new Date());
    const today = new Date();

    if (!isAfter(curDate, minDate)) {
      errorCode.value = '06';
    }

    // Future DOB not allowed
    if (isAfter(curDate, today)) {
      errorCode.value = '05'; 
    }

  }
  // Set isDateSupported based on whether errorCode has a value
  isDateSupported.value = !errorCode.value;
};

const validateLicense = () => {
  const val = myLicense.value;
  isLicenseValid.value = (val.length > 6) && patterns.namePattern.test(val) && val.length <= 8;
};

const validatePlate = () => {
  const val = myPlateNumber.value;

  const max = myPlateIdType.value === 'title' ? 8 : 7;

  isPlateValid.value = ((val.length > 0 && val.length <= max) && patterns.inputPattern.test(val));
};

const validateType = () => {
  isTypeValid.value = (myPlateType.value.code !== 'none');
};

const validateVin = () => {
  const val = myLastVin.value;
  isVinValid.value = (val.length === 6 && patterns.inputPattern.test(val));
};

const validateCaseNumber = () => {
  const val = myCaseNumber.value;
  isCaseNumberValid.value = (val.length > 0) && patterns.caseNumPattern.test(val) && val.length <= 18 ;
};

const validateViolationSuspensionDate = () => {
  violationErrorCode.value = undefined;

  let violationValue = myViolationDate.value;
  let suspensionValue = mySuspensionDate.value;

  // Early exit: both missing
  if (!violationValue && !suspensionValue) {
    violationErrorCode.value = '12';
    isViolationDateSupported.value = false;
    return;
  }

  // Date values
  violationValue = dateValue(violationValue);
  suspensionValue = dateValue(suspensionValue);

  const minDate = toDate(new Date(1751, 11, 31, 23, 59, 59));
  const maxDate = toDate(new Date());

  // Validate violation date
  if (violationValue) {
    const curDateViolation = parse(violationValue, 'yyyy-MM-dd', new Date());
    if (!isValidDate(curDateViolation, minDate, maxDate)) {
      violationErrorCode.value = '12';
    }
  }

  // Validate suspension date
  if (suspensionValue) {
    const curDateSuspension = parse(suspensionValue, 'yyyy-MM-dd', new Date());
    if (!isAfter(curDateSuspension, minDate)) {
      violationErrorCode.value = '12';
    }
  }

  isViolationDateSupported.value = !violationErrorCode.value;
};

const dateValue = (value) => {
  if (!value) return value;

  const isMMDDYYYY = /\d{2}\/\d{2}\/\d{4}$/g.test(value);
  if (!isMMDDYYYY) return value;

  return `${value.substr(6, 4)}-${value.substr(0, 2)}-${value.substr(3, 2)}`;
};

const isValidDate = (date, minDate, maxDate) => {
  return isAfter(date, minDate) && isAfter(maxDate, date);
};

const validateCrashDate = () => {
  crashDateErrorCode.value = undefined; 
  let value = myCrashDate.value;      

  if (value == null || value == '') {
    crashDateErrorCode.value = '14';
  } else {
    // Convert MM/DD/YYYY to YYYY-MM-DD format if detected
    if ((value?.match(/\d{2}\/\d{2}\/\d{4}$/g) || []).length === 1) {
      const tmp = value;
      value = `${tmp.substr(6, 4)}-${tmp.substr(0, 2)}-${tmp.substr(3, 2)}`;
    }
    
    const minDate = toDate(new Date(1751, 11, 31, 23, 59, 59));
    const maxDate = toDate(new Date()); 

    if (value != null) { 
      const curDate = parse(value, 'yyyy-MM-dd', new Date());

      // Validate if the parsed date is after the minimum allowed date
      if (!isAfter(curDate, minDate)) {
        crashDateErrorCode.value = '14';
      }
      // Validate if the parsed date is not in the future
      if (!isAfter(maxDate, curDate)) {
        crashDateErrorCode.value = '14';
      }
    }
  }
  isCrashDateSupported.value = !crashDateErrorCode.value;
};

const validateCrashLocation = () => {
  isCrashLocationValid.value = (myCrashLocation.value.code !== 'none');
};

const validate = () => {
  if (props.selectId == 1) { // Access selectId.value
    validateName();
    validateSsn();
    validateDob();
    validateLicense();
    return isNameValid.value && isSsnValid.value && isDateSupported.value && isLicenseValid.value;
  } else if (props.selectId == 2) {
    validateName();
    validateDob();
    validatePlate();
    validateType();
    validateVin();
    return isNameValid.value && isDateSupported.value && isPlateValid.value && isTypeValid.value && isVinValid.value;
  } else if (props.selectId == 3) {
    validateName();
    validateDob();
    validateCaseNumber();
    validateViolationSuspensionDate();
    return isNameValid.value && isDateSupported.value && isCaseNumberValid.value && isViolationDateSupported.value;
  } else if (props.selectId == 4) {
    validateName();
    validateDob();
    validateCrashDate();
    validateCrashLocation();
    return isNameValid.value && isDateSupported.value && isCrashDateSupported.value && isCrashLocationValid.value;
  }
  return false; // Default return if no conditions met
};

const validateBusiness = () => {
  validateOrgName();

  if (props.businessSelectId == 2) {
    // VEHICLE LOGIN
    if (myPlateIdType.value === 'plate') {
      validatePlate();
      validateType();
      validateVin();
      return (isOrgNameValid.value && isPlateValid.value && isTypeValid.value && isVinValid.value);
    }

    if (myPlateIdType.value === 'title') {
      // Title number uses the same input as plateNumber
      validatePlate(); 
      validateVin();
      return (isOrgNameValid.value && isPlateValid.value && isVinValid.value);
    }
  }

  if (props.businessSelectId == 3) {
    validateCaseNumber();
    validateViolationSuspensionDate();
    return (isOrgNameValid.value && isCaseNumberValid.value && isViolationDateSupported.value);
  }

  if (props.businessSelectId == 4) {
    validateCrashDate();
    validateCrashLocation();
    return (isOrgNameValid.value && isCrashDateSupported.value && isCrashLocationValid.value);
  }

  return false;
};

const getFormReset = () => {
  // Reset form data fields
  orgName.value = '';
  myName.value = '';
  myLastSsn.value = '';
  myDob.value = '';
  myDobForPicker.value = '';
  myLicense.value = '';
  myPlateNumber.value = '';
  myPlateType.value = { code: 'none' };
  myLastVin.value = '';
  myCaseNumber.value = '';
  myViolationDate.value = '';
  myViolationDateForPicker.value = '';
  mySuspensionDate.value = '';
  mySuspensionDateForPicker.value = '';
  myCrashDate.value = '';
  myCrashDateForPicker.value = '';
  myCrashLocation.value = { code: 'none' };

  myPlateIdType.value = 'plate';   // reset to default for business users

  // Reset validation flags
  isOrgNameValid.value =true;
  isNameValid.value = true;
  isSsnValid.value = true;
  isDateSupported.value = true;
  isModify.value = false;
  errorCode.value = undefined;
  isLicenseValid.value = true;
  isddNumberValid.value = true;
  isPlateValid.value = true;
  isTypeValid.value = true;
  isVinValid.value = true;
  isCaseNumberValid.value = true;
  isViolationDateSupported.value = true;
  isSuspensionDateSupported.value = true;
  isCrashDateSupported.value = true;
  isCrashLocationValid.value = true;
  violationErrorCode.value = undefined;
  crashDateErrorCode.value = undefined;

  // Reset Vuex store state.networkResponse
  store.state.networkResponse = null; 
};

defineExpose({
  getFormReset
});

const handlePopstate = (event) => {
  console.log(" this is ", event);
  // Call the validate function without the event argument
  validate(); 
  validateBusiness();
};

// Lifecycle hooks
onMounted(() => {
  sourceReferrer.value = getReservationReferrerOnly();
  window.addEventListener('popstate', handlePopstate);
});

onUnmounted(() => {
  window.removeEventListener('popstate', handlePopstate);
});

onBeforeRouteUpdate((to, from) => {
  console.log("New route:", to); 
  console.log("Old route:", from); 

  validate(); 
  validateBusiness();
});
</script>


<style lang="scss" scoped>
  .quote-lookup-form {
    select, input {
      min-width: 230px;
    }
    input.short {
      min-width: unset;
      max-width: 6rem;
    }
  }
  .req-field{
    margin-top: 1rem;
  }
  .my-custom-tooltip {
    max-width: 280px;
    white-space: normal;
    word-break: break-word;
  }

  .app-input-group {
    .app-btn-image {
      width: 44px;
      height: 44px;
      margin-left: 0.45em;
      img {
        width: 18px;
      }
    }
    .inputclass {
      margin-bottom: 12px;
      .subText {
        font-size: 13px;
      }
    }

    .license-image {
      width: 100%;
    }

    .d-flex{
      display: flex;
    }

    .tooltip {
      .tooltip-inner {
        max-width: 400px !important;
      }
    }

    .org-input-class{
      width: 100%;
    }
    .org-req-err{
      min-width: 100% !important;
    }
  }
  .fourdigitssn{
    width: 6.25em;
    border: 1px solid #293557;
    border-radius: 6px;
    height: 2.5em;
    padding: 0.5em 1em;
  }
  /* Hide password reveal button in MS Edge */
  ::-ms-reveal {
    display: none;
  }
  .conjunction {
    // do not use flex alignment due to conditional error <p>
    line-height: 2em;
    padding: 0 0.75em;
    margin-top: 2.5rem;
    @media (min-width: 355px) and (max-width: 500px) {
      margin-top: 0em;
      width: 60%;
      margin-left: 4em;
    }
  }

  .app-error-message-violation-suspension{
    color: var(--ri-red);
  }
  .app-input-model{
    margin-bottom: 0em !important;
  }
  .app-error-group{
    padding-top: 0em !important;
  }
  .app-input-empty-row{
    margin-bottom: 0.75em;
  }

  .date-input-wrapper {
    position: relative;
    display: inline-block;
    width: 100%;

    input[type="text"] {
      width: 100%;
      padding-right: 45px;
      box-sizing: border-box;
    }

    .app-calendar-btn {
      position: absolute;
      right: 0;
      top: 50%;
      transform: translateY(-50%);
      padding: 0;
      border: none;
      background: transparent;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 44px;
      height: 44px;
      margin-right: 0.25em;
      z-index: 1;

      img {
        width: 18px;
        height: 18px;
        pointer-events: none;
      }

      &:hover {
        opacity: 0.8;
      }

      &:focus {
        outline: 2px solid var(--ri-blue, #0056b3);
        outline-offset: 2px;
        border-radius: 4px;
      }
    }
  }

  .hidden-date-picker {
    position: absolute;
    top: 0;
    left: 0;
    width: 44px;
    height: 44px;
    opacity: 0;
    cursor: pointer;
    z-index: 2;
    /* Allow pointer events so mobile taps activate native date picker
       Input sits over the calendar button (invisible) to open OS picker. */
    pointer-events: auto;
  }

  @media only screen and (min-width: 500px) and (max-width: 743px) {
  .org-input-class {
    width: 420px !important;
  }
}
</style>